import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getStaffWithTimeCache,
  getServicesCategoryWithTimeCache } from '../../APICalling/APICalling';
import { checkBookingAvailability } from '../bookingAvailability';
import { getSessionStorageValue, setSessionStorageValue, removeSessionStorageValue,
  STORAGE_KEYS } from '../SessionStorage';

const INITIAL_STATE = { operation: null, availabilityError: '', serviceRefreshError: '' };
// Prefer the API's own message (e.g. {"status":false,"message":"Endpoint not found."});
// the caller's fallback text is only for failures without one (network drop, timeout).
const getApiErrorMessage = (error) => {
  const payload = error?.response?.data;
  const message = payload?.message ?? payload?.data?.message;
  return typeof message === 'string' ? message.trim() : '';
};
const matchesOutlet = (block, outlet, type) => block &&
  String(block.outlet) === String(outlet) && String(block.outletType) === String(type);
const getServicesKey = (members) => JSON.stringify(members.map((member) => [
  String(member.id),
  (member.services || []).map((service) => [
    String(service.id), String(service.duration ?? ''),
    String(service.therapistData?.id ?? ''),
    (service.selectedAddons || []).map((addon) => [String(addon.id), String(addon.duration ?? '')]),
  ]),
]));
const matchesBlock = (block, outlet, type, servicesKey, hasServices) =>
  matchesOutlet(block, outlet, type) && (
    typeof block.servicesKey === 'string'
      ? block.servicesKey === servicesKey || block.clearedServicesKey === servicesKey
      // Older blocks have no selection key. Retain them only for the cleared cart.
      : !hasServices
  );

export const useGuestAvailability = ({ members, selectedOutletID, outletTypeID, onContinue, onNoTherapists }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const servicesKey = useMemo(() => getServicesKey(members), [members]);
  const hasServices = members.some((member) => (member.services?.length ?? 0) > 0);
  const [state, setState] = useState(() => {
    const block = getSessionStorageValue(STORAGE_KEYS.AVAILABILITY_BLOCK);
    return matchesBlock(block, selectedOutletID, outletTypeID, servicesKey, hasServices) && typeof block.message === 'string'
      ? { ...INITIAL_STATE, availabilityError: block.message } : INITIAL_STATE;
  });
  const mounted = useRef(false);
  const pending = useRef(false);
  const requestId = useRef(0);
  // Context updates replace members immutably. Comparing these references
  // avoids serializing all service/catalog data on every render.
  const selection = useMemo(() => ({ members, selectedOutletID, outletTypeID }),
    [members, selectedOutletID, outletTypeID]);
  const latestSelection = useRef(selection);
  useLayoutEffect(() => { latestSelection.current = selection; }, [selection]);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestId.current += 1;
      pending.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    const block = getSessionStorageValue(STORAGE_KEYS.AVAILABILITY_BLOCK);
    if (block && !matchesBlock(block, selectedOutletID, outletTypeID, servicesKey, hasServices)) {
      removeSessionStorageValue(STORAGE_KEYS.AVAILABILITY_BLOCK);
      setState(INITIAL_STATE);
    }
  }, [selectedOutletID, outletTypeID, servicesKey, hasServices]);

  const persistBlock = useCallback((message, servicesCleared = false) => {
    setSessionStorageValue(STORAGE_KEYS.AVAILABILITY_BLOCK, {
      message, outlet: selectedOutletID, outletType: outletTypeID, servicesKey,
      ...(servicesCleared && {
        clearedServicesKey: getServicesKey(members.map((member) => ({ ...member, services: [] }))),
      }),
    });
  }, [selectedOutletID, outletTypeID, servicesKey, members]);

  const canContinue = !state.availabilityError && !!selectedOutletID && members.length > 0 &&
    members.every((member) => (member.services?.length ?? 0) > 0);

  const runRequest = useCallback(async (operation, request, onSuccess, errorMessage) => {
    if (!mounted.current || pending.current || !selectedOutletID) return;
    pending.current = true;
    const id = ++requestId.current;
    const isCurrent = () => mounted.current && requestId.current === id;
    const isSelectionCurrent = () => isCurrent() && latestSelection.current === selection;
    setState((previous) => ({ ...previous, operation, serviceRefreshError: '',
      ...(operation === 'check' ? { availabilityError: '' } : {}) }));
    try {
      const result = await request();
      if (isSelectionCurrent()) onSuccess(result);
    } catch (error) {
      if (isSelectionCurrent()) {
        const message = getApiErrorMessage(error) || errorMessage;
        if (operation === 'check') persistBlock(message);
        setState((previous) => ({ ...previous,
          [operation === 'check' ? 'availabilityError' : 'serviceRefreshError']: message }));
      }
    } finally {
      if (isCurrent()) {
        pending.current = false;
        setState((previous) => ({ ...previous, operation: null }));
      }
    }
  }, [selectedOutletID, selection, persistBlock]);

  const handleContinue = useCallback(() => {
    if (!canContinue) return;
    return runRequest('check', () => checkBookingAvailability({
      services: members.flatMap((member) => member.services),
      loadStaff: (service) => {
        const params = { role: 2, availability: 0, outlet: selectedOutletID,
          outlet_type: outletTypeID, services: service.id, duration: service.duration,
          pagination: 0, status: 1, leave: 0 };
        return queryClient.fetchQuery({ queryKey: ['staff', params],
          queryFn: () => getStaffWithTimeCache(params), staleTime: 0, retry: false });
      },
    }), (reason) => {
      if (reason) {
        onNoTherapists?.();
        const message = 'No therapists are available for the selected services. Please choose another service or outlet.';
        persistBlock(message, Boolean(onNoTherapists));
        setState((previous) => ({ ...previous, availabilityError: message }));
      } else {
        removeSessionStorageValue(STORAGE_KEYS.AVAILABILITY_BLOCK);
        onContinue();
        navigate('/time');
      }
    }, 'Unable to check availability. Please try again.');
  }, [canContinue, runRequest, selectedOutletID, outletTypeID, members, queryClient, onContinue, onNoTherapists, navigate, persistBlock]);

  const handleChooseAnotherService = useCallback(() => runRequest('refresh', async () => {
    // Same key/params as SelectServices: refresh once and reuse on arrival.
    const params = { pagination: 0, status: 1, outlet_type: outletTypeID,
      outlet: selectedOutletID, is_image: 1 };
    const response = await queryClient.fetchQuery({ queryKey: ['serviceCategories', params],
      queryFn: () => getServicesCategoryWithTimeCache(params), staleTime: 0, retry: false });
    if (response?.data?.success === false || response?.data?.data?.success === false) {
      // Carry the payload so the catch surfaces the API's own message.
      const error = new Error('Unable to load services');
      error.response = { data: response.data };
      throw error;
    }
    return response;
  }, () => {
    removeSessionStorageValue(STORAGE_KEYS.AVAILABILITY_BLOCK);
    setState(INITIAL_STATE);
    navigate('/select-services');
  }, 'Unable to load services. Please try again.'),
  [runRequest, outletTypeID, selectedOutletID, queryClient, navigate]);

  return { ...state, canContinue, isCheckingAvailability: state.operation === 'check',
    isRefreshingServices: state.operation === 'refresh',
    handleContinue, handleChooseAnotherService };
};
